import { StrictMode } from "react";
import { createRoot } from "react-dom/client";

import "./posthog";

import "./index.css";
// Initialize TWA settings (immersive mode, dark theme)
import { App } from "./App.tsx";
import { ErrorBoundary } from "./components/error-boundary/ErrorBoundary";

import "@trackman/golf-web-ui-components/style";

import "./styles/design-tokens.all.css";
import "./styles/global.css";
import "./styles/theme.css";
import "./styles/touch.css";
import "./twa-init";
import { applyRadarSystemConfigOverrideFromQueryParam } from "./features/radar/services/radar-port-override/apply-radar-port-override-from-query-param";
import { initServiceWorker } from "./registerSW";
import { livenessHeartbeat } from "./services/monitoring/liveness-heartbeat/liveness-heartbeat";
import { memoryWatchdog } from "./services/monitoring/memory-watchdog/memory-watchdog";
import { perfMetricsMonitor } from "./services/monitoring/perf-metrics-monitor/perf-metrics-monitor";
import { reportStartupMetrics } from "./services/monitoring/startup-metrics/startup-metrics";
import { storageHealthMonitor } from "./services/monitoring/storage-health-monitor/storage-health-monitor";
import { simpleUpdateManager } from "./services/pwa/simple-update-manager/simple-update-manager";
import { initSentry, registerGlobalErrorHandlers } from "./utils/sentry";
import {
  consoleReporter,
  createCompositeReporter,
  createPerfMonitorReporter,
  setReporter,
} from "./utils/web-vitals";
import { initWebVitals } from "./utils/web-vitals";
import { installZoomLock } from "./utils/zoom-lock";

// Initialize Sentry first (before anything else can throw)
initSentry();
registerGlobalErrorHandlers();
storageHealthMonitor.start();
if (!import.meta.env.DEV) {
  perfMetricsMonitor.start();
  memoryWatchdog.start();
  livenessHeartbeat.start();
}

// Prevent context menu on long press (Android Chrome)
document.addEventListener("contextmenu", (e) => {
  e.preventDefault();
});

installZoomLock();
void applyRadarSystemConfigOverrideFromQueryParam(window.location.search);

// Service Worker: register in production, unregister in development
if ("serviceWorker" in navigator) {
  if (import.meta.env.DEV) {
    void (async () => {
      try {
        const registrations = await navigator.serviceWorker.getRegistrations();
        const results = await Promise.allSettled(
          registrations.map((r) => r.unregister())
        );
        const unregistered = results.filter(
          (r) => r.status === "fulfilled" && r.value
        ).length;
        if (unregistered > 0) {
          console.log(
            `🧹 Unregistered ${unregistered} stale Service Worker(s) for dev mode`
          );
        }
      } catch (error) {
        console.warn(
          "⚠️ Failed to get Service Worker registrations in dev mode",
          error
        );
      }
    })();
  } else {
    initServiceWorker();
    simpleUpdateManager.startPolling();
  }
} else {
  console.warn("⚠️ Service Worker not supported");
}

// Wire web vitals into the live perf debug store (+ console in dev)
setReporter(
  createCompositeReporter(
    createPerfMonitorReporter(
      perfMetricsMonitor.reportVital.bind(perfMetricsMonitor)
    ),
    ...(import.meta.env.DEV ? [consoleReporter] : [])
  )
);

// Initialize Web Vitals monitoring
initWebVitals();

if (!import.meta.env.DEV) {
  const idle =
    window.requestIdleCallback ?? ((cb: () => void) => setTimeout(cb, 2000));
  idle(() => void reportStartupMetrics());
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </StrictMode>
);
